import { useEffect, useRef, useState, type ReactNode } from "react";
import {
  WAVE_WORKFLOW_STATUSES,
  isWaveArchived,
  type WaveCoverKeys,
  type WaveParticipantRecord,
  type WaveLastSeenRecord,
  type WavePresenceRecord,
  type WaveRecord,
  type WaveStatus,
  type WaveThreadView,
  type WaveTimelineOrder,
} from "@vida/wave-core";
import { WaveCoverBanner } from "../library/WaveCoverBanner";
import { WaveFavoriteButton } from "../shell/WaveBrandHeader";
import { WaveShellToggleButton } from "../shell/WaveShellToggleButton";
import { WaveFeedLayoutRow } from "./WaveFeedLayoutRow";
import { WavePresenceOverlay } from "./WavePresenceOverlay";
import { requestWaveCover } from "../../services/waveCoverRequest";
import { getPublicBaseUrl } from "../../config";

type Props = {
  wave: WaveRecord;
  isFavorite: boolean;
  showPlayback: boolean;
  presences: WavePresenceRecord[];
  lastSeen?: WaveLastSeenRecord[];
  currentUserSub: string;
  currentUserEmail?: string;
  participants: WaveParticipantRecord[];
  onToggleFavorite: () => void;
  onRename: () => void;
  onArchiveToggle: () => void;
  onStatusChange: (status: WaveStatus) => void;
  onTogglePlayback: () => void;
  onInvite: () => void;
  onCoverKeyChange?: (waveId: string, keys: WaveCoverKeys) => void;
  onBack?: () => void;
  backLabel?: string;
  sidebarCollapsed?: boolean;
  onReopenSidebar?: () => void;
  threadView?: WaveThreadView;
  onThreadViewChange?: (view: WaveThreadView) => void;
  timelineOrder?: WaveTimelineOrder;
  onTimelineOrderToggle?: () => void;
  archivedCount?: number;
  onShowArchivedWidgets?: () => void;
  allCollapsed?: boolean;
  onToggleCollapseAll?: () => void;
  tocVisible?: boolean;
  onToggleToc?: () => void;
};

function HeaderOverflowMenu({
  wave,
  archived,
  showPlayback,
  onRename,
  onArchiveToggle,
  onTogglePlayback,
  onCoverKeyChange,
  onShowArchived,
  archivedCount = 0,
}: {
  wave: WaveRecord;
  archived: boolean;
  showPlayback: boolean;
  onRename: () => void;
  onArchiveToggle: () => void;
  onTogglePlayback: () => void;
  onCoverKeyChange?: (waveId: string, keys: WaveCoverKeys) => void;
  onShowArchived?: () => void;
  archivedCount?: number;
}) {
  const [open, setOpen] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  const hasCover = Boolean(
    wave.coverImageS3Key?.trim() || wave.coverImageHeaderS3Key?.trim(),
  );

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      if (!wrapRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [open]);

  const run = (action: () => void) => {
    setOpen(false);
    action();
  };

  const copyLink = () => {
    const url = `${getPublicBaseUrl()}/w/${wave.waveId}`;
    void navigator.clipboard.writeText(url).then(() => {
      setLinkCopied(true);
      window.setTimeout(() => setLinkCopied(false), 2500);
    });
  };

  const regenerateCover = () => {
    void requestWaveCover(wave, hasCover, (keys) => {
      onCoverKeyChange?.(wave.waveId, keys);
    });
  };

  return (
    <div className="wave-header-overflow-wrap" ref={wrapRef}>
      <button
        type="button"
        className="wave-header-overflow-btn wave-menu-toast-btn"
        aria-label="Altre azioni"
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={() => setOpen((v) => !v)}
      >
        <span aria-hidden>⋯</span>
      </button>
      {open && (
        <div className="wave-header-overflow-menu" role="menu">
          {!showPlayback && (
            <>
              <button type="button" role="menuitem" onClick={() => run(copyLink)}>
                {linkCopied ? "Link copiato ✓" : "Copia link"}
              </button>
              <button type="button" role="menuitem" onClick={() => run(regenerateCover)}>
                {hasCover ? "Rigenera cover" : "Genera cover"}
              </button>
              <hr />
            </>
          )}
          <button type="button" role="menuitem" onClick={() => run(onRename)}>
            Rinomina
          </button>
          {!showPlayback && onShowArchived && archivedCount > 0 && (
            <button type="button" role="menuitem" onClick={() => run(onShowArchived)}>
              Widget archiviati ({archivedCount})
            </button>
          )}
          <button type="button" role="menuitem" onClick={() => run(onArchiveToggle)}>
            {archived ? "Ripristina" : "Archivia"}
          </button>
          <hr />
          <button
            type="button"
            role="menuitem"
            className={showPlayback ? "is-active" : ""}
            onClick={() => run(onTogglePlayback)}
          >
            {showPlayback ? "Esci da playback" : "Playback"}
          </button>
        </div>
      )}
    </div>
  );
}

function WaveEditorToolbarPills({
  wave,
  archived,
  showPlayback,
  threadView,
  onThreadViewChange,
  timelineOrder,
  onTimelineOrderToggle,
  allCollapsed,
  onToggleCollapseAll,
  tocVisible,
  onToggleToc,
  onStatusChange,
  presences,
  currentUserSub,
  currentUserEmail,
  participants,
  lastSeen,
  onInvite,
  onRename,
  onArchiveToggle,
  onTogglePlayback,
  onCoverKeyChange,
  onShowArchivedWidgets,
  archivedCount,
}: {
  wave: WaveRecord;
  archived: boolean;
  showPlayback: boolean;
  threadView: WaveThreadView;
  onThreadViewChange?: (view: WaveThreadView) => void;
  timelineOrder: WaveTimelineOrder;
  onTimelineOrderToggle?: () => void;
  allCollapsed: boolean;
  onToggleCollapseAll?: () => void;
  tocVisible?: boolean;
  onToggleToc?: () => void;
  onStatusChange: (status: WaveStatus) => void;
  presences: WavePresenceRecord[];
  currentUserSub: string;
  currentUserEmail: string;
  participants: WaveParticipantRecord[];
  lastSeen: WaveLastSeenRecord[];
  onInvite: () => void;
  onRename: () => void;
  onArchiveToggle: () => void;
  onTogglePlayback: () => void;
  onCoverKeyChange?: (waveId: string, keys: WaveCoverKeys) => void;
  onShowArchivedWidgets?: () => void;
  archivedCount: number;
}) {
  return (
    <div className="wave-editor-toolbar-pills">
      {!showPlayback && onThreadViewChange && (
        <div className="wave-thread-view-toggle" role="group" aria-label="Vista thread">
          <button
            type="button"
            className={threadView === "timeline" ? "is-active" : ""}
            onClick={() => onThreadViewChange("timeline")}
          >
            Timeline
          </button>
          <button
            type="button"
            className={threadView === "story" ? "is-active" : ""}
            onClick={() => onThreadViewChange("story")}
          >
            Storia
          </button>
        </div>
      )}
      {!showPlayback && threadView === "timeline" && onTimelineOrderToggle && (
        <button
          type="button"
          className={`wave-timeline-order-btn${timelineOrder === "desc" ? " is-active" : ""}`}
          aria-pressed={timelineOrder === "desc"}
          onClick={onTimelineOrderToggle}
        >
          {timelineOrder === "desc" ? "Più recenti in alto ✓" : "Più recenti in alto"}
        </button>
      )}
      {!showPlayback && onToggleCollapseAll && (
        <button
          type="button"
          className="wave-collapse-all-btn"
          aria-pressed={allCollapsed}
          onClick={onToggleCollapseAll}
        >
          {allCollapsed ? "Espandi tutto" : "Comprimi tutto"}
          <span className="wave-collapse-all-btn-chevron" aria-hidden>
            {allCollapsed ? "▾" : "▴"}
          </span>
        </button>
      )}
      {!showPlayback && onToggleToc && (
        <button
          type="button"
          className={`wave-toc-toggle-btn${tocVisible ? " is-active" : ""}`}
          aria-pressed={tocVisible}
          onClick={onToggleToc}
        >
          {tocVisible ? "Indice ✓" : "Indice"}
        </button>
      )}
      {!showPlayback && (
        <select
          className="wave-header-status-select"
          value={wave.status}
          disabled={archived}
          onChange={(e) => onStatusChange(e.target.value as WaveStatus)}
          aria-label="Stato workflow"
        >
          {WAVE_WORKFLOW_STATUSES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      )}
      <div className="wave-editor-header-bar-pills-actions">
        <div className="wave-editor-header-bar-presence">
          <WavePresenceOverlay
            presences={presences}
            currentSub={currentUserSub}
            currentUserEmail={currentUserEmail}
            participants={participants}
            lastSeen={lastSeen}
            ownerSub={wave.ownerSub}
          />
        </div>
        {!showPlayback && (
          <button type="button" className="wave-share-btn" onClick={onInvite}>
            Condividi
          </button>
        )}
        <HeaderOverflowMenu
          wave={wave}
          archived={archived}
          showPlayback={showPlayback}
          onRename={onRename}
          onArchiveToggle={onArchiveToggle}
          onTogglePlayback={onTogglePlayback}
          onCoverKeyChange={onCoverKeyChange}
          onShowArchived={onShowArchivedWidgets}
          archivedCount={archivedCount}
        />
      </div>
    </div>
  );
}

export function WaveEditorHeaderBar({
  wave,
  isFavorite,
  showPlayback,
  presences,
  lastSeen = [],
  currentUserSub,
  currentUserEmail = "",
  participants,
  onToggleFavorite,
  onRename,
  onArchiveToggle,
  onStatusChange,
  onTogglePlayback,
  onInvite,
  onCoverKeyChange,
  onBack,
  backLabel,
  sidebarCollapsed = false,
  onReopenSidebar,
  threadView = "timeline",
  onThreadViewChange,
  timelineOrder = "asc",
  onTimelineOrderToggle,
  archivedCount = 0,
  onShowArchivedWidgets,
  allCollapsed = false,
  onToggleCollapseAll,
  tocVisible = false,
  onToggleToc,
}: Props) {
  const archived = isWaveArchived(wave);
  const showNavChrome =
    Boolean(onBack) || (sidebarCollapsed && Boolean(onReopenSidebar));
  const sentinelRef = useRef<HTMLDivElement>(null);
  const [toolbarStuck, setToolbarStuck] = useState(false);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || typeof IntersectionObserver === "undefined") return;
    const scrollRoot = sentinel.closest(".main");
    const observer = new IntersectionObserver(
      ([entry]) => {
        setToolbarStuck(!entry?.isIntersecting);
      },
      {
        root: scrollRoot,
        threshold: 0,
      },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);

  const toolbarPills = (
    <WaveEditorToolbarPills
      wave={wave}
      archived={archived}
      showPlayback={showPlayback}
      threadView={threadView}
      onThreadViewChange={onThreadViewChange}
      timelineOrder={timelineOrder}
      onTimelineOrderToggle={onTimelineOrderToggle}
      allCollapsed={allCollapsed}
      onToggleCollapseAll={onToggleCollapseAll}
      tocVisible={tocVisible}
      onToggleToc={onToggleToc}
      onStatusChange={onStatusChange}
      presences={presences}
      currentUserSub={currentUserSub}
      currentUserEmail={currentUserEmail}
      participants={participants}
      lastSeen={lastSeen}
      onInvite={onInvite}
      onRename={onRename}
      onArchiveToggle={onArchiveToggle}
      onTogglePlayback={onTogglePlayback}
      onCoverKeyChange={onCoverKeyChange}
      onShowArchivedWidgets={onShowArchivedWidgets}
      archivedCount={archivedCount}
    />
  );

  const mainContent: ReactNode = (
    <>
      <div className="wave-editor-header-hero">
        <header className="wave-editor-header-bar">
          <WaveCoverBanner
            wave={wave}
            variant="headerBackground"
            onCoverKeyChange={onCoverKeyChange}
          />
          <div className="wave-editor-header-bar-content wave-editor-header-bar-content--hero">
            <div className="wave-editor-header-bar-top">
              <div className="wave-editor-header-bar-title">
                <h1>{wave.title}</h1>
                <span className="wave-status-badge">{archived ? "Archiviata" : wave.status}</span>
                {!showPlayback && (
                  <WaveFavoriteButton
                    className="wave-editor-favorite"
                    active={isFavorite}
                    label={isFavorite ? "Rimuovi dai preferiti" : "Aggiungi ai preferiti"}
                    onToggle={onToggleFavorite}
                  />
                )}
              </div>
            </div>
          </div>
        </header>
      </div>
      <div
        ref={sentinelRef}
        className="wave-editor-toolbar-sentinel"
        aria-hidden
      />
      <div
        className={`wave-editor-toolbar${toolbarStuck ? " is-stuck" : ""}`}
      >
        {toolbarStuck && (
          <div className="wave-editor-toolbar-inline-title">
            <h2 className="wave-editor-toolbar-title">{wave.title}</h2>
            <span className="wave-status-badge wave-editor-toolbar-status">
              {archived ? "Archiviata" : wave.status}
            </span>
          </div>
        )}
        {toolbarPills}
      </div>
    </>
  );

  return (
    <div className="wave-editor-header-bar-wrap">
      <WaveFeedLayoutRow
        className="wave-feed-grid--header"
        gutter={
          showNavChrome ? (
            <div className="wave-editor-header-chrome">
              {sidebarCollapsed && onReopenSidebar && (
                <WaveShellToggleButton
                  expanded={false}
                  className="sidebar-reopen-btn"
                  onClick={onReopenSidebar}
                />
              )}
              {onBack && (
                <button
                  type="button"
                  className="wave-back-icon-btn"
                  aria-label={backLabel ? `Torna a ${backLabel}` : "Indietro"}
                  title={backLabel ? `Torna a ${backLabel}` : "Indietro"}
                  onClick={onBack}
                >
                  ←
                </button>
              )}
            </div>
          ) : undefined
        }
        main={mainContent}
      />
    </div>
  );
}
